import { useCallback, useMemo, useState } from 'react';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Group, Loader, Table, Text, UnstyledButton } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { buildConnectionSyncColumns } from './connection-sync-columns';
import { DisabledControlTooltip } from './explanatory-tooltip';
import { useConnectionLogoProps } from '../../../../../../../inventory-sync/hooks/use-connection-logo-props';
import { messages } from './connection-sync-table.messages';
import style from './connection-sync-table.module.css';

import type { ConnectionSyncColumn } from './connection-sync-columns';
import type { SortState } from '@shipstation/client-common/components/data-grid';
import type { ProductConnectionSyncRow } from '../../product-inventory-sync-types';

const TABLE_MIN_WIDTH = 900;

const sortRows = (rows: readonly ProductConnectionSyncRow[], sort: SortState | null): ProductConnectionSyncRow[] => {
  if (!sort) {
    return [...rows];
  }

  const directionMultiplier = sort.direction === 'desc' ? -1 : 1;

  return [...rows].sort((left, right) => directionMultiplier * left.connectionName.localeCompare(right.connectionName));
};

const getNextSort = (current: SortState | null, field: string): SortState | null => {
  if (current?.field !== field) {
    return { field, direction: 'asc' };
  }

  return current.direction === 'asc' ? { field, direction: 'desc' } : null;
};

type SortableHeaderProps = {
  column: ConnectionSyncColumn;
  sort: SortState | null;
  onSortChange: (sort: SortState | null) => void;
};

const SortableHeader = ({ column, sort, onSortChange }: SortableHeaderProps) => {
  const activeDirection = sort?.field === column.key ? sort.direction : null;

  const handleClick = useCallback(() => onSortChange(getNextSort(sort, column.key)), [onSortChange, sort, column.key]);

  return (
    <UnstyledButton className={style['sort-button']} onClick={handleClick}>
      {column.header}
      {activeDirection && <FontAwesomeIcon className={style['sort-icon']} icon={activeDirection === 'asc' ? faCaretUp : faCaretDown} />}
    </UnstyledButton>
  );
};

type ConnectionSyncTableProps = {
  rows: readonly ProductConnectionSyncRow[];
  isLoading: boolean;
};

export const ConnectionSyncTable = ({ rows, isLoading }: ConnectionSyncTableProps) => {
  const intl = useIntl();
  const getLogoProps = useConnectionLogoProps();
  const [sort, setSort] = useState<SortState | null>({ field: 'connectionName', direction: 'asc' });

  const columns = useMemo(() => buildConnectionSyncColumns({ intl, getLogoProps }), [intl, getLogoProps]);

  const sortedRows = useMemo(() => sortRows(rows, sort), [rows, sort]);

  const eligibleRows = useMemo(() => rows.filter(({ ineligibleReason }) => ineligibleReason === null), [rows]);

  return (
    <>
      <Group className={style['table-header']} justify='space-between'>
        <Text fw={600} size='sm'>
          <FormattedMessage {...messages.targetConnectionsHeading} values={{ count: eligibleRows.length }} />
        </Text>
        <DisabledControlTooltip label={intl.formatMessage(messages.perProductSyncUnavailable)}>
          <Button disabled variant='secondary'>
            <FormattedMessage {...messages.syncNowButton} />
          </Button>
        </DisabledControlTooltip>
      </Group>
      <Table.ScrollContainer className={style['table-container']} minWidth={TABLE_MIN_WIDTH} type='native'>
        <Table horizontalSpacing='1.5' verticalSpacing='sm'>
          <Table.Thead>
            <Table.Tr className={style['header-row']}>
              {columns.map((column) => (
                <Table.Th className={style['header-cell']} key={column.key} ta={column.align}>
                  {column.sortable ? <SortableHeader column={column} onSortChange={setSort} sort={sort} /> : column.header}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && sortedRows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <Group justify='center'>
                    <Loader size='sm' />
                  </Group>
                </Table.Td>
              </Table.Tr>
            ) : (
              sortedRows.map((row) => (
                <Table.Tr key={row.connectionId}>
                  {columns.map((column) => (
                    <Table.Td key={column.key} ta={column.align}>
                      {column.render(row)}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </>
  );
};
